import { useRef, useState, useEffect, useContext, useCallback } from "react";
import { FormBuilder } from '@react-form-builder/designer'
import { BuilderView, define, event, number, string } from '@react-form-builder/core'
import {
  components as muiComponents,
  MuiViewerWrapper,
  materialUiComponentsDescriptions,
} from '@react-form-builder/components-material-ui'
import {
  rsImage,
  rSuiteComponentsDescriptions,
} from '@react-form-builder/components-rsuite'
import { signatureComponent, signatureComponentsDescriptions } from '@react-form-builder/components-signature'
import { useNavigate, useParams, useLocation, UNSAFE_NavigationContext } from "react-router-dom";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

import BlueButton from "src/Components/Common/bluebutton";
import { getImageUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import WOFormService from "src/Services/API/woform.service";
import FormTemplateService from "src/Services/API/formtemplate.service";
import UserService from "src/Services/API/user.service";
import RSCSLogo from "./customComponents/RSCSLogo";
import TechnicianDropdown from "./customComponents/TechnicianDropdown";

const excludedTypes = ['MuiDatePicker', 'MuiTimePicker', 'MuiDateTimePicker', 'MuiUploader', 'Uploader']

const filteredMuiComponents = muiComponents.filter(
  c => !excludedTypes.includes(c.meta.type)
)

const rscsLogoComponent = define(RSCSLogo, 'RSCSLogo')
  .name('RSCS Logo')
  .category('Custom')
  .props({
    width: number.default(140),
    height: string.default('auto'),
    alt: string.default('RSCS Logo'),
  })

const technicianDropdownComponent = define(TechnicianDropdown, 'TechnicianDropdown')
  .name('Technician Dropdown')
  .category('Custom')
  .props({
    value: string.valued,
    woId: number.default(0),
    label: string.default('Technician'),
    placeholder: string.default('Select technician'),
    onChange: event,
  })

const extendedBuilderView = new BuilderView([
  ...filteredMuiComponents,
  rsImage.build(),
  signatureComponent.build(),
  rscsLogoComponent.build(),
  technicianDropdownComponent.build(),
])
  .withViewerWrapper(MuiViewerWrapper)
  .withComponentLibraryDescription(materialUiComponentsDescriptions)
  .withComponentLibraryDescription(rSuiteComponentsDescriptions)
  .withComponentLibraryDescription(signatureComponentsDescriptions)

const isNonEmptyForm = (formJSON) => {
  if (!formJSON) return false;
  try {
    const parsed = typeof formJSON === "string" ? JSON.parse(formJSON) : formJSON;
    const children = parsed?.form?.children;
    return Array.isArray(children) && children.length > 0;
  } catch {
    return false;
  }
};

const FormBuilderComponent = () => {
  const { woId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const formId = location.state?.formId;
  const formName = location.state?.formName || location.state?.templateName || '';
  const formSchema = location.state?.formSchema;
  const isTemplateMode = location.state?.mode === "template";
  const templateId = location.state?.templateId;
  const templateDraft = location.state?.templateDraft;
  const backPath = location.state?.from || "/form-templates";
  const customerId = location.state?.customerId;
  const formType = location.state?.formType;
  const frequency = location.state?.frequency;
  const status = location.state?.status;

  const builderRef = useRef(null);
  const blockingRef = useRef(true);
  const pendingNavRef = useRef(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const goBackPath = isTemplateMode
    ? backPath
    : (woId ? `/work/${woId}` : (backPath || "/all-forms"));

  const hasUnsavedContent = useCallback(() => {
    try {
      const formJSON = builderRef.current?.formAsString;
      return isNonEmptyForm(formJSON);
    } catch {
      return false;
    }
  }, []);

  // Intercept in-app navigation (navigate(), <Link>, sidebar clicks)
  useEffect(() => {
    if (!navigator) return;
    const { push: origPush, replace: origReplace, go: origGo } = navigator;

    navigator.push = (...args) => {
      if (blockingRef.current && hasUnsavedContent()) {
        pendingNavRef.current = () => origPush.apply(navigator, args);
        setShowLeaveModal(true);
        return;
      }
      origPush.apply(navigator, args);
    };

    navigator.replace = (...args) => {
      if (blockingRef.current && hasUnsavedContent()) {
        pendingNavRef.current = () => origReplace.apply(navigator, args);
        setShowLeaveModal(true);
        return;
      }
      origReplace.apply(navigator, args);
    };

    navigator.go = (...args) => {
      if (blockingRef.current && hasUnsavedContent()) {
        pendingNavRef.current = () => origGo.apply(navigator, args);
        setShowLeaveModal(true);
        return;
      }
      origGo.apply(navigator, args);
    };

    return () => {
      navigator.push = origPush;
      navigator.replace = origReplace;
      navigator.go = origGo;
    };
  }, [navigator, hasUnsavedContent]);

  // Intercept browser back/forward button via popstate.
  // Push a guard entry so the back button pops that entry (not the real one),
  // keeping us on the page while we decide whether to show the modal.
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (!blockingRef.current || !hasUnsavedContent()) {
        // No content to protect — navigate away
        navigate(goBackPath);
        return;
      }
      // Re-push the guard entry so we stay on the page
      window.history.pushState(null, '', window.location.href);
      pendingNavRef.current = () => {
        navigate(goBackPath);
      };
      setShowLeaveModal(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [goBackPath, navigate, hasUnsavedContent]);

  // Warn on browser close / refresh only if form has content
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedContent()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedContent]);

  useEffect(() => {
    window.__makaniFormWoId = woId ? Number(woId) : null;
    return () => {
      delete window.__makaniFormWoId;
    };
  }, [woId]);

  const setRef = useCallback((builder) => {
    if (builder) {
      builderRef.current = builder;
    }
  }, []);

  const getForm = formSchema
    ? () => (typeof formSchema === "string" ? formSchema : JSON.stringify(formSchema))
    : undefined;

  const proceedNavigation = useCallback(() => {
    blockingRef.current = false;
    setShowLeaveModal(false);
    if (pendingNavRef.current) {
      const nav = pendingNavRef.current;
      pendingNavRef.current = null;
      nav();
    }
  }, []);

  const handleLeave = useCallback(() => {
    proceedNavigation();
  }, [proceedNavigation]);

  const handleStay = useCallback(() => {
    pendingNavRef.current = null;
    setShowLeaveModal(false);
  }, []);

  const handleBackClick = useCallback(() => {
    navigate(goBackPath);
  }, [navigate, goBackPath]);

  const navigateAfterFormSave = useCallback(() => {
    blockingRef.current = false;
    const fromPath = location.state?.from;
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (woId) {
      navigate(`/work/${woId}`);
      return;
    }
    navigate("/all-forms");
  }, [location.state?.from, woId, navigate]);

  const saveForm = useCallback(() => {
    if (!builderRef.current) {
      console.log("Builder not found");
      return;
    }
    const formJSON = builderRef.current.formAsString;
    let parsedSchema = formJSON;
    try {
      parsedSchema = JSON.parse(formJSON);
    } catch (e) {
      // Keep raw string if parsing fails.
    }

    if (isTemplateMode) {
      if (templateId) {
        FormTemplateService.update(templateId, {
          ...(templateDraft || {}),
          schema: parsedSchema,
        })
          .then(() => {
            blockingRef.current = false;
            toast("Form saved successfully");
            navigate(backPath);
          })
          .catch((error) => {
            handleError(error);
          });
        return;
      }

      if (!templateDraft) {
        toast("Template details are missing");
        return;
      }

      const currentUser = UserService.getCurrentUser();
      FormTemplateService.create({
        ...templateDraft,
        schema: parsedSchema,
        createdBy: currentUser?.id,
        customerId,
      })
        .then(() => {
          blockingRef.current = false;
          toast("Template saved successfully");
          navigate(backPath);
        })
        .catch((error) => {
          handleError(error);
        });
      return;
    }

    if (formId) {
      WOFormService.update(formId, { schema: parsedSchema })
        .then(() => {
          blockingRef.current = false;
          toast("form has updated successfully");
          navigateAfterFormSave();
        })
        .catch((error) => {
          handleError(error);
        });
      return;
    }

    const currentUser = UserService.getCurrentUser();

    WOFormService.save({
      woId,
      name: formName,
      schema: formJSON,
      formType,
      frequency,
      status: woId ? (status ?? 1) : status,
      templateId,
      createdBy: currentUser?.id,
    })
      .then(() => {
        blockingRef.current = false;
        toast("Form saved successfully");
        navigateAfterFormSave();
      })
      .catch((error) => {
        handleError(error);
      });
  }, [isTemplateMode, templateId, templateDraft, backPath, customerId, formId, woId, formName, formType, frequency, status, navigate, navigateAfterFormSave]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div
          className="flex items-center cursor-pointer"
          onClick={handleBackClick}
        >
          <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" />
          <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
        </div>
        <div className="w-[100px]">
          <BlueButton onClicked={saveForm} title="Save form" />
        </div>
      </div>
      <div style={{ height: "83vh", width: "100%" }} className='border-2 border-gray-300'>
        <FormBuilder
          builderRef={setRef}
          view={extendedBuilderView}
          {...(getForm ? { getForm } : {})}
          licenseKey={process.env.REACT_APP_FORM_ENGINE_LICENSE_KEY}
          theme="light"
        />
      </div>

      {showLeaveModal && (
        <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <OutsideClickHandler onOutsideClick={handleStay}>
            <div className="w-[400px] bg-white rounded-[6px] flex flex-col">
              <div className="border-b-[1px] border-borderColor py-3 px-4">
                <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor">
                  UNSAVED CHANGES
                </p>
              </div>
              <div className="p-[18px]">
                <p className="text-[12px] font-OpenSansRegular text-subTitleColor mb-6">
                  You are leaving the page without saving the form. Are you sure?
                </p>
                <div className="flex gap-3">
                  <div
                    onClick={handleLeave}
                    className="flex-1 h-[34px] text-[11px] rounded-[6px] border-[1px] border-red-400 flex items-center justify-center text-red-500 cursor-pointer font-OpenSansRegular font-[600]"
                  >
                    LEAVE
                  </div>
                  <div
                    onClick={handleStay}
                    className="flex-1 h-[34px] text-[11px] rounded-[6px] bg-blueColor flex items-center justify-center text-white cursor-pointer font-OpenSansRegular font-[600]"
                  >
                    STAY
                  </div>
                </div>
              </div>
            </div>
          </OutsideClickHandler>
        </div>
      )}
    </div>
  );
};

export default FormBuilderComponent;